"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.math = exports.getTicketTotalWithLowerTierSubAndOtherItems = exports.getTicketTotalWithExtraLaborQtys = exports.getTicketTotal = exports.calculateMarkupPercentage = exports.calculatePercent = exports.getOtherItemsPricingTotal = exports.getLowerTierSubPricingTotal = exports.getNonStandardMaterialPricingTotal = exports.getMaterialPricingTotal = exports.getLaborPricingWithExtraQtys = exports.getLaborPricingTotal = exports.LABOR_MAPPING = exports.getEquipmentHoursTotal = exports.getEquipmentPricingTotal = exports.multiplyArray = exports.sumArray = exports.calculateArray = exports.sumObjTotal = exports.sumObjTotalFromPaths = exports.sumObjProperty = exports.costForQuantityRow = exports.lineTotal = exports.bankersRound = void 0;
const utils_1 = require("./utils");
const json_ptr_1 = require("json-ptr");
/**
 * Round using Banker's Rounding (e.g. even decimals get rounded down, odd decimals rounded up)
 * Examples:
 *    bankersRound(1.5) === 2
 *    bankersRound(2.5) === 2
 *    bankersRound(1.535, 2) === 1.54
 *    bankersRound(1.525, 2) === 1.52
 * @param { number } value The decimal to round
 * @param { number } decimalPlaces The number of decimal places to round to
 */
const bankersRound = (value, decimalPlaces = 0) => {
    var x = value * Math.pow(10, decimalPlaces);
    var r = Math.round(x);
    var br = Math.abs(x) % 1 === 0.5 ? (r % 2 === 0 ? r : r - 1) : r;
    return br / Math.pow(10, decimalPlaces);
};
exports.bankersRound = bankersRound;
/**
 * Calculate a line total given a unit price and amount. The result will be
 * given in whole cents.
 * @param { number } price Value of a given item
 * @param { number } quantity Quantity of that item
 * @returns { number } Price x Quantity
 * @example lineTotal(1000, 5) => 5000
 */
const lineTotal = (price, quantity) => {
    // if either price or amount is not a number, return 0
    if (isNaN(price) || isNaN(quantity))
        return 0;
    // round the value to zero decimals since we're working with integer cents
    return (0, exports.bankersRound)(price * quantity);
};
exports.lineTotal = lineTotal;
/**
 * Calculate a line total given a quantity, hours and unit price. The result will be
 * given in whole cents.
 * @param { number } price Value of a given item in cents
 * @param { number } hours Number of Hours
 * @param { number } quantity Quantity of that item
 * @returns { number } Price x Quantity x Hours
 * @example costForQuantityRow(1000, 10, 5) => 50000
 */
const costForQuantityRow = (price, hours, quantity) => {
    // if either price or amount is not a number, return 0
    if (isNaN(price) || isNaN(quantity) || isNaN(hours))
        return 0;
    // round the value to zero decimals since we're working with integer cents
    return (0, exports.bankersRound)(hours * price * quantity);
};
exports.costForQuantityRow = costForQuantityRow;
/**
 * Given an array of objects and a property name, returns the sum of that
 * property's value. If the property value is an object, sums value of the
 * object's keys. Deeper nesting is not supported.
 * @param { Array<Record<string, any>> } arrObj Array of objects to sum
 * @param { string } property Value to be summed
 * @returns { number } Total Sum of all objects in array
 * @example sumObjProperty([{quantity: 2}, {quantity: 3}], 'quantity') => 5
 */
const sumObjProperty = (arrObj, property) => {
    if (!(arrObj === null || arrObj === void 0 ? void 0 : arrObj.length))
        return 0;
    return arrObj.reduce((acc, obj) => {
        if (typeof obj[property] !== "object") {
            let val = obj[property] ? obj[property] : 0;
            val = typeof val === "string" ? Number(val) : val;
            return acc + val;
        }
        return Object.keys(obj[property] ? obj[property] : {}).reduce((_acc, key) => {
            let val = obj[property][key] ? obj[property][key] : 0;
            val = typeof val === "string" ? Number(val) : val;
            return val + _acc;
        }, acc);
    }, 0);
};
exports.sumObjProperty = sumObjProperty;
/**
 * Similar to sumObjTotal only you can include multiple paths for quantities and it uses JSON paths.
 * In example you can see how you can generate a mapping within a template using the makeArray helper
 * `quantityPaths` and `mapping` can be submitted as either arrays or jsonified strings that can be converted to arrays.
 * See jsonified string in the second example, the lists are wrapped in quotes, and unlike using makeArray, you need commas and brackets for the lists
 *
 * @param arrObj List to be summed - the list is expected to be a list of objects
 * @param pricingPath A json pointer path to either the pricing value or an object holding multiple pricing keys. This path is relative to the list item and should use / as a separator for nested keys
 * @param quantityPaths A list of json pointer paths containing either quantity objects or a quantity number value
 * @param mapping Optional list of lists for pairing quantity keys and pricing objects - like matching hour type with pricing type. Quantity path is the key and is relative to list item, and the pricing path is the value is relative to the pricing object.
 * @returns The summed total of all list items where the pricing is multiplied with all of the relevant quantity values
 * @example {{sumObjTotalFromPaths subcontractor_use.labor "/employee" (makeArray "/hours" "/crew_size") (makeArray (makeArray "st" "st_price") (makeArray "ot" "ot_price") (makeArray "dt" "dt_price") (makeArray "pot" "pot_price") (makeArray "pdt" "pdt_price") ) }}
 * @example {{sumObjTotalFromPaths subcontractor_use.labor "/employee" '["/hours", "/crew_size"]' '[["st", "st_price"], ["ot", "ot_price"], ["dt", "dt_price") ["pot", "pot_price"] ["pdt", "pdt_price"]]' }}

*/
const sumObjTotalFromPaths = (arrObj, pricingPath, quantityPaths, mapping) => {
    if (!(arrObj === null || arrObj === void 0 ? void 0 : arrObj.length) || !pricingPath || !(quantityPaths === null || quantityPaths === void 0 ? void 0 : quantityPaths.length))
        return 0;
    if (typeof mapping === "string") {
        try {
            mapping = JSON.parse(mapping);
        }
        catch (error) {
            mapping = [];
        }
    }
    let rawQtyPriceMapping = mapping !== null && mapping !== void 0 ? mapping : [];
    // convert to a map for quick lookup
    const qtyPricingMapping = typeof rawQtyPriceMapping[Symbol.iterator] === "function" ? new Map(rawQtyPriceMapping) : new Map();
    if (typeof quantityPaths === "string") {
        // if we get a string for quantity paths, see if we can convert it to a list
        try {
            quantityPaths = JSON.parse(quantityPaths);
        }
        catch (error) {
            // if not try plugging it into a list
            // possibly an error where they only submitted one path without making it into a list
            quantityPaths = [quantityPaths];
        }
    }
    return arrObj.reduce((acc, obj) => {
        pricingPath = pricingPath.startsWith("/") ? pricingPath : `/${pricingPath}`;
        // pricing data can be an obj with number values (like employee with pricing fields for T&M) or a number
        const pricing = json_ptr_1.JsonPointer.get(obj, pricingPath);
        // quantity data can be an obj with number values (like hours for T&M) or a number value
        // we split the two data types into different groups so we can handle them accordingly
        const numberQtys = [];
        let objQty = {};
        // @ts-ignore - it is complaining that this may be a string, but the check above covers that, this is a list
        quantityPaths.forEach(path => {
            var _a;
            // handlebars passes through the makeArray helper with the quantityPath args - so skip unless a string
            if (typeof path !== "string")
                return;
            const newQty = ((_a = json_ptr_1.JsonPointer.get(obj, path)) !== null && _a !== void 0 ? _a : 0);
            if (typeof newQty === "number") {
                if (!(qtyPricingMapping === null || qtyPricingMapping === void 0 ? void 0 : qtyPricingMapping.get(path)))
                    numberQtys.push(newQty);
                else
                    objQty[path] = newQty;
            }
            else if (typeof newQty === "object") {
                // combine the quantity objects together
                objQty = Object.assign(Object.assign({}, objQty), newQty);
            }
        });
        // if we don't have any quantities to use, nothing more to do
        if (!(numberQtys === null || numberQtys === void 0 ? void 0 : numberQtys.length) && !Object.keys(objQty)) {
            return acc;
        }
        // if we are only dealing with numbers, we can do some basic multiplication
        if (!Object.keys(objQty) && typeof pricing === "number") {
            return (0, exports.multiplyArray)([pricing, ...numberQtys]);
        }
        // Otherwise, we need to match pricing and object keys and calculate the
        // sum of all their subtotals
        if (qtyPricingMapping && typeof pricing !== "number") {
            return Object.keys(objQty).reduce((_acc, key) => {
                var _a, _b, _c;
                const pricingKey = (_a = qtyPricingMapping.get(key)) !== null && _a !== void 0 ? _a : "";
                const linePrice = (_b = json_ptr_1.JsonPointer.get(pricing, pricingKey.startsWith("/") ? pricingKey : `/${pricingKey}`)) !== null && _b !== void 0 ? _b : 0;
                const lineQuantity = (_c = objQty === null || objQty === void 0 ? void 0 : objQty[key]) !== null && _c !== void 0 ? _c : 0;
                return (0, exports.multiplyArray)([(0, exports.lineTotal)(linePrice, lineQuantity), ...numberQtys]) + _acc;
            }, acc);
        }
        return Object.keys(objQty ? objQty : {}).reduce((_acc, key) => {
            var _a;
            const linePrice = typeof pricing === "object" ? pricing === null || pricing === void 0 ? void 0 : pricing[key] : pricing !== null && pricing !== void 0 ? pricing : 0;
            const lineQuantity = (_a = objQty === null || objQty === void 0 ? void 0 : objQty[key]) !== null && _a !== void 0 ? _a : 0;
            return (0, exports.multiplyArray)([(0, exports.lineTotal)(linePrice, lineQuantity), ...numberQtys]) + _acc;
        }, acc);
    }, 0);
};
exports.sumObjTotalFromPaths = sumObjTotalFromPaths;
/**
 * Given an array of objects, the key(s) of its pricing data, the key(s) of its
 * quantity data and (optionally) a mapping of quantity key names to pricing key
 * names, calculates the total price for all the objects. The mapping should be
 * an array that can be passed to JavaScript's `Map` constructor.
 *
 * @param { Array<Record<string, any>> } arrObj Array of objects to sum
 * @param { Array<string> } pricingKeys Array of top level keys where pricing info is stored
 * @param { Array<string> } quantityKeys Array of top level keys where quantity info is stored
 * @param { Record<string, any> } mapping Object mapping of what quantity keys match what pricing keys
 * @returns { number } Summed value of the objects
 * @example const arr = [{
 *                 employee: {
 *                         pricing: {
 *                                 standardTime: 1000,
 *                                 overTime: 2000,
 *                                 doubleTime: 3000,
 *                         }
 *                 },
 *                 hours: {
 *                         st: 5,
 *                         ot: 2,
 *                         dt: 1,
 *                 }
 *         }]
 *
 *         const mapping = [
 *                 ['st', 'standardTime'],
 *                 ['ot', 'overTime'],
 *                 ['dt', 'doubleTime'],
 *         ]
 *         // (1000 * 5) + (2000 * 2) + (3000 * 1)
 *         sumObjTotal(arr, ['employee', 'pricing'], ['hours'], mapping) === 12000
 */
const sumObjTotal = (arrObj, pricingKeys, quantityKeys, mapping // couldn't find right type to please Symbol.iterator
) => {
    if (!(arrObj === null || arrObj === void 0 ? void 0 : arrObj.length))
        return 0;
    return arrObj.reduce((acc, obj) => {
        let pricing = pricingKeys.reduce((pricing_accum, key) => (pricing_accum ? pricing_accum[key] : 0), obj);
        const quantity = quantityKeys.reduce((quantity_accum, key) => (quantity_accum ? quantity_accum[key] : 0), obj);
        if (quantity == null) {
            return acc;
        }
        // If the price and quantity are scalar values, we can sum their products
        if (typeof quantity !== "object" && typeof pricing === "number") {
            return acc + (0, exports.lineTotal)(pricing, quantity);
        }
        // Otherwise, we need to match pricing and object keys and calculate the
        // sum of all their subtotals
        if (mapping && typeof mapping[Symbol.iterator] === "function") {
            const map = new Map(mapping);
            return Object.keys(quantity).reduce((_acc, key) => {
                var _a, _b;
                const pricingKey = (_a = map.get(key)) !== null && _a !== void 0 ? _a : "";
                const linePrice = (_b = pricing === null || pricing === void 0 ? void 0 : pricing[pricingKey]) !== null && _b !== void 0 ? _b : 0;
                const lineQuantity = quantity[key] ? quantity[key] : 0;
                return (0, exports.lineTotal)(linePrice, lineQuantity) + _acc;
            }, acc);
        }
        return Object.keys(quantity ? quantity : {}).reduce((_acc, key) => {
            const linePrice = pricing[key] ? pricing[key] : 0;
            const lineQuantity = quantity[key] ? quantity[key] : 0;
            return (0, exports.lineTotal)(linePrice, lineQuantity) + _acc;
        }, acc);
    }, 0);
};
exports.sumObjTotal = sumObjTotal;
/**
 * Given a list with alternating number and math operator values, calculate the resulting value.
 * This will calculate in the order provided - so don't rely on order of operations.
 * @param arrayToCalc list with alternating number and math operator values, first and last value must be numbers
 * @returns {number} total of the array calculated in the order provided
 * @example {{ calculateArray 5 "+" 10 "-" 3 "*" 2 }} => 24 ex  5 + 10 = 15 - 3 = 12 * 2 = 24
 */
const calculateArray = (...arrayToCalc) => {
    if (typeof arrayToCalc[arrayToCalc.length - 1] === "object") {
        // remove the handlebars options object
        arrayToCalc.pop();
    }
    var value = arrayToCalc[0];
    arrayToCalc.forEach((v, index) => {
        // the odd values will be the calculation symbols
        if (index % 2 === 1) {
            const operator = v;
            const nextValue = Number(arrayToCalc[index + 1]);
            if (operator === "*") {
                value = value * nextValue;
            }
            else if (operator === "+") {
                value = value + nextValue;
            }
            else if (operator === "-") {
                value = value - nextValue;
            }
            else if (operator === "/") {
                value = value / nextValue;
            }
        }
    });
    return value;
};
exports.calculateArray = calculateArray;
/**
 * Given an array of number it will return sum of all numbers
 * @param { Array<number> } arrToSum Array of objects to sum
 * @returns { number } Sum of all values in the array
 * @example sumArray([1, 2, 3, 4]) => 10
 */
const sumArray = (arrToSum) => {
    if (!(arrToSum === null || arrToSum === void 0 ? void 0 : arrToSum.length))
        return 0;
    return arrToSum.reduce((total, num) => {
        return (0, utils_1.zeroIfNaN)(total) + (0, utils_1.zeroIfNaN)(num);
    });
};
exports.sumArray = sumArray;
/**
 * Given an array of number it will return total of all numbers multiplied together
 * @param { Array<number> } arrToMultiply Array of objects to multiply
 * @returns { number } Total of all values in the array multiplied
 * @example multiplyArray([1, 2, 3, 4]) => 24
 */
const multiplyArray = (arrToMultiply) => {
    if (!(arrToMultiply === null || arrToMultiply === void 0 ? void 0 : arrToMultiply.length))
        return 0;
    return (0, exports.bankersRound)(arrToMultiply.reduce((total, num) => {
        return (0, utils_1.zeroIfNaN)(total) * (0, utils_1.zeroIfNaN)(num);
    }));
};
exports.multiplyArray = multiplyArray;
/**
 * Checks for the expected equipment fields before looking for pricing. Not an exported helper.
 * @ignore
 * @param { Record<string, any> } obj Equipment item to inspect
 * @returns { boolean } If the object has all expected fields, will return true, otherwise false.
 * @example hasExpectedEquipmentFields({
 *    "hours": {
 *      "running_time": 5
 *    },
 *    "equipment": {
 *      "id": 1,
 *      "equipment_id": "EQ-123"
 *    },
 *    "quantity": 2
 * }) => true
 * @example hasExpectedEquipmentFields({
 *    "hours": {
 *      "running_time": 5
 *    },
 *    "quantity": 2
 * }) => false
 */
const hasExpectedEquipmentFields = (obj) => {
    const fields = ["hours", "equipment", "quantity"];
    let hasValues = true;
    for (const field of fields) {
        if (!(obj === null || obj === void 0 ? void 0 : obj[field])) {
            hasValues = false;
            break;
        }
    }
    return hasValues;
};
/**
 * Given a priced equipment list will return the total price of all the items in the list
 * @param { Array<Record<string, any>> } equipment List of equipment objects with pricing information
 * @returns { number } Total value of all equipment in the list based on hours, quantity, and price in cents
 */
const getEquipmentPricingTotal = (equipment) => {
    if (!(equipment === null || equipment === void 0 ? void 0 : equipment.length))
        return 0;
    return equipment.reduce((acc, obj) => {
        const equip = obj;
        if (!hasExpectedEquipmentFields(equip)) {
            return 0;
        }
        const rowTotal = (0, exports.sumArray)([
            (0, exports.multiplyArray)([equip.hours.idle, equip.equipment.idle_time_price, equip.quantity]),
            (0, exports.multiplyArray)([equip.hours.over, equip.equipment.over_time_price, equip.quantity]),
            (0, exports.multiplyArray)([equip.hours.running, equip.equipment.running_time_price, equip.quantity]),
        ]);
        return acc + rowTotal;
    }, 0);
};
exports.getEquipmentPricingTotal = getEquipmentPricingTotal;
/**
 * Given an equipment list will return the total hours of all the items in the list
 * @param { Array<Record<string, any>> } equipment List of equipment objects with hours information
 * @returns { number } Total number of hours of all equipment in the list
 */
const getEquipmentHoursTotal = (equipment) => {
    if (!(equipment === null || equipment === void 0 ? void 0 : equipment.length))
        return 0;
    return equipment.reduce((acc, obj) => {
        const equip = obj;
        if (!hasExpectedEquipmentFields(equip)) {
            return acc + 0;
        }
        return (acc +
            (0, exports.sumArray)([
                (0, exports.multiplyArray)([equip.hours.idle ? equip.hours.idle : 0, equip.quantity]),
                (0, exports.multiplyArray)([equip.hours.over ? equip.hours.over : 0, equip.quantity]),
                (0, exports.multiplyArray)([equip.hours.running ? equip.hours.running : 0, equip.quantity]),
            ]));
    }, 0);
};
exports.getEquipmentHoursTotal = getEquipmentHoursTotal;
exports.LABOR_MAPPING = [
    ["st", "st_price"],
    ["ot", "ot_price"],
    ["dt", "dt_price"],
    ["pot", "pot_price"],
    ["pdt", "pdt_price"],
];
/**
 * Given a list of labor objects (employee + hours) return the price of the labor based on employee prices and hours
 * @param { Array<Record<string, any>> } labor - list of priced employees and hours
 * @returns { Number } total price of all provided labor
 * @example {{ getLaborPricingTotal subcontractor_use.labor }}
 */
const getLaborPricingTotal = (labor) => {
    if (!(labor === null || labor === void 0 ? void 0 : labor.length))
        return 0;
    return (0, exports.sumObjTotal)(labor, ["employee"], ["hours"], exports.LABOR_MAPPING);
};
exports.getLaborPricingTotal = getLaborPricingTotal;
/**
 * Given a list of labor objects (employee + hours) plus additional quantity path values
 * Additional mappings can also be included if the additional quantity values are an obj.
 * Those mappings should be lists with two strings, the first is the quantity key, which is relative to the list item object,
 * and the pricing key second, which is relative to the employee object. See second example adding crew_size/crew_price
 * return the price of the labor based on employee prices and hours and any additional quantity keys provided
 * @param { Array<Record<string, any>> } labor - list of priced employees, hours, and other qty values
 * @param {Array<string>} qtyPaths - list of jsonpointer strings to additional quantity values beyond hours
 * @param { Array<Array<string>> } additionaMapping Optional list of lists for pairing pricing keys and quantity objects - like matching pricing with hour type
 * @returns { Number } total price of all provided labor
 * @example {{getLaborPricingWithExtraQtys subcontractor_use.labor (makeArray "/crew_size") }}
 * @example {{getLaborPricingWithExtraQtys subcontractor_use.labor (makeArray "/crew/crew_size") (makeArray (makeArray "/crew_size" "/crew_price") ) }}
 */
const getLaborPricingWithExtraQtys = (labor, qtyPaths = [], additionaMapping = []) => {
    if (!(labor === null || labor === void 0 ? void 0 : labor.length))
        return 0;
    if (typeof qtyPaths === "string") {
        // if we get a string for quantity paths, see if we can convert it to a list
        try {
            qtyPaths = JSON.parse(qtyPaths);
        }
        catch (error) {
            // if not try plugging it into a list
            // possibly an error where they only submitted one path without making it into a list
            qtyPaths = [qtyPaths];
        }
    }
    const laborMapping = Array.isArray(additionaMapping) ? [...exports.LABOR_MAPPING, ...additionaMapping] : exports.LABOR_MAPPING;
    return (0, exports.sumObjTotalFromPaths)(labor, "/employee", ["/hours", ...qtyPaths], laborMapping);
};
exports.getLaborPricingWithExtraQtys = getLaborPricingWithExtraQtys;
/**
 * Given an array of material objects, return the price of the materials based on unit price and quantity
 * @param { Array<Record<string, any>> } materials - list of material objects
 * @returns { Number } total price of all provided materials
 */
const getMaterialPricingTotal = (materials) => {
    if (!(materials === null || materials === void 0 ? void 0 : materials.length))
        return 0;
    return (0, exports.sumObjTotal)(materials, ["material", "unit_price"], ["quantity"]);
};
exports.getMaterialPricingTotal = getMaterialPricingTotal;
/**
 * Given an array of non-standard material objects, return the price based on cost and quantity
 * @param { Array<Record<string, any>> } nonstandard_materials - list of non-standard material objects
 * @returns { Number } total price of all provided non-standard materials
 */
const getNonStandardMaterialPricingTotal = (nonstandard_materials) => {
    if (!(nonstandard_materials === null || nonstandard_materials === void 0 ? void 0 : nonstandard_materials.length))
        return 0;
    return (0, exports.sumObjTotal)(nonstandard_materials, ["cost"], ["quantity"]);
};
exports.getNonStandardMaterialPricingTotal = getNonStandardMaterialPricingTotal;
/**
 * Given an array of lower tier sub objects, return the price based on unit_rate and quantity
 * @param { Array<Record<string, any>> } lower_tier_sub - list of lower tier sub objects
 * @returns { Number } total price of all provided lower tier sub
 */
const getLowerTierSubPricingTotal = (lower_tier_sub) => {
    if (!(lower_tier_sub === null || lower_tier_sub === void 0 ? void 0 : lower_tier_sub.length))
        return 0;
    return (0, exports.sumObjTotal)(lower_tier_sub, ["unit_rate"], ["quantity"]);
};
exports.getLowerTierSubPricingTotal = getLowerTierSubPricingTotal;
/**
 * Given an array of other item objects, return the price based on cost and quantity
 * @param { Array<Record<string, any>> } other_items - list of other items objects
 * @returns { Number } total price of all provided other items
 */
const getOtherItemsPricingTotal = (other_items) => {
    if (!(other_items === null || other_items === void 0 ? void 0 : other_items.length))
        return 0;
    return (0, exports.sumObjTotal)(other_items, ["cost"], ["quantity"]);
};
exports.getOtherItemsPricingTotal = getOtherItemsPricingTotal;
/**
 * Given a price and a percentage will return the percentage value in cents
 * @param { number } price Base price in cents
 * @param { number } percent The percentage you want from the base price
 * @returns { number } The value of (price * percentage)/100
 * @example calculatePercent(1000, 10.5) => 105
 */
const calculatePercent = (price, percent) => {
    if (!price || !percent) {
        return 0;
    }
    return (0, exports.bankersRound)((price * percent) / 100);
};
exports.calculatePercent = calculatePercent;
/**
 * Given the base total for a Labor, Equipment, or Material list get the markup percentage in cents
 * @param { Record<string, any> } markups Object folding markup information
 * @param { number } sectionTotal Base total for a given section that will have markups applied
 * @returns { number } value of the markup based on sectionTotal
 * @example calculateMarkupPercentage({
 *      "bond": 5,
 *      "fee": 2
 *    },
 *    200
 * ) => 14
 */
const calculateMarkupPercentage = (markups, sectionTotal) => {
    let markupTotal = 0;
    Object.values(markups).forEach(markup => {
        markupTotal += (0, exports.calculatePercent)(sectionTotal, markup);
    });
    return markupTotal;
};
exports.calculateMarkupPercentage = calculateMarkupPercentage;
/**
 * Given an object with labor, equipment, material, or non_standard_material arrays that include pricing info
 * will return the total value of the whole object, including any supplied markups in labor_markup, equipment_markup, or material_markup
 * @param { Record<string, any> } pricedStore Object with priced LEM arrays and possibly markups
 * @returns { number } Total value of the given pricedStore object
 */
const getTicketTotal = (pricedStore) => {
    var _a, _b;
    let totalCost = 0;
    const labor = pricedStore === null || pricedStore === void 0 ? void 0 : pricedStore.labor;
    if (labor) {
        const laborTotal = (0, exports.getLaborPricingTotal)(labor);
        totalCost += laborTotal;
        const laborMarkups = pricedStore === null || pricedStore === void 0 ? void 0 : pricedStore.labor_markup;
        if (laborMarkups) {
            totalCost += (0, exports.calculateMarkupPercentage)(laborMarkups, laborTotal);
        }
    }
    const equipment = (_a = pricedStore === null || pricedStore === void 0 ? void 0 : pricedStore.Equipment) !== null && _a !== void 0 ? _a : pricedStore === null || pricedStore === void 0 ? void 0 : pricedStore.equipment;
    if (equipment) {
        const equipTotal = (0, exports.getEquipmentPricingTotal)(equipment);
        totalCost += equipTotal;
        const equipMarkups = pricedStore === null || pricedStore === void 0 ? void 0 : pricedStore.equipment_markup;
        if (equipMarkups) {
            totalCost += (0, exports.calculateMarkupPercentage)(equipMarkups, equipTotal);
        }
    }
    const material = (_b = pricedStore === null || pricedStore === void 0 ? void 0 : pricedStore.Material) !== null && _b !== void 0 ? _b : pricedStore === null || pricedStore === void 0 ? void 0 : pricedStore.material;
    if (material) {
        const materialTotal = (0, exports.getMaterialPricingTotal)(material);
        totalCost += materialTotal;
        const materialMarkups = pricedStore === null || pricedStore === void 0 ? void 0 : pricedStore.material_markup;
        if (materialMarkups) {
            totalCost += (0, exports.calculateMarkupPercentage)(materialMarkups, materialTotal);
        }
    }
    const nonstandard_material = pricedStore === null || pricedStore === void 0 ? void 0 : pricedStore.non_standard_material;
    if (nonstandard_material) {
        const nonstandardMaterialTotal = (0, exports.getNonStandardMaterialPricingTotal)(nonstandard_material);
        totalCost += nonstandardMaterialTotal;
        const nonstandardMaterialMarkups = pricedStore === null || pricedStore === void 0 ? void 0 : pricedStore.non_standard_material_markup;
        if (nonstandardMaterialMarkups) {
            totalCost += (0, exports.calculateMarkupPercentage)(nonstandardMaterialMarkups, nonstandardMaterialTotal);
        }
    }
    return totalCost;
};
exports.getTicketTotal = getTicketTotal;
/**
 * A special one-off helper to add additional quantity keys to just the labor calculation.
 * Using `getLaborPricingWithExtraQtys` instead of `getLaborPricingTotal`
 * @param { Record<string, any> } pricedStore Object with priced LEM arrays and possibly markups
 * @param { Array<string> } laborQtyPaths list of jsonpointer paths to additional labor quantities beyond hours
 * @param { Array<Array<string>> } mapping Optional mapping for pairing pricing keys and quantity objects - like matching pricing with hour type
 * @returns { number } Total value of the given pricedStore object
 * @example {{getTicketTotalWithExtraLaborQtys subcontractor_use.labor (makeArray "/crew_size") }}
 * @example {{getTicketTotalWithExtraLaborQtys subcontractor_use.labor (makeArray "/crew/crew_size") (makeArray (makeArray "/crew_size" "/crew_price") ) }}
 */
const getTicketTotalWithExtraLaborQtys = (pricedStore, laborQtyPaths, mapping = []) => {
    var _a, _b;
    let totalCost = 0;
    const labor = pricedStore === null || pricedStore === void 0 ? void 0 : pricedStore.labor;
    if (labor) {
        const laborTotal = (0, exports.getLaborPricingWithExtraQtys)(labor, laborQtyPaths, mapping);
        totalCost += laborTotal;
        const laborMarkups = pricedStore === null || pricedStore === void 0 ? void 0 : pricedStore.labor_markup;
        if (laborMarkups) {
            totalCost += (0, exports.calculateMarkupPercentage)(laborMarkups, laborTotal);
        }
    }
    const equipment = (_a = pricedStore === null || pricedStore === void 0 ? void 0 : pricedStore.Equipment) !== null && _a !== void 0 ? _a : pricedStore === null || pricedStore === void 0 ? void 0 : pricedStore.equipment;
    if (equipment) {
        const equipTotal = (0, exports.getEquipmentPricingTotal)(equipment);
        totalCost += equipTotal;
        const equipMarkups = pricedStore === null || pricedStore === void 0 ? void 0 : pricedStore.equipment_markup;
        if (equipMarkups) {
            totalCost += (0, exports.calculateMarkupPercentage)(equipMarkups, equipTotal);
        }
    }
    const material = (_b = pricedStore === null || pricedStore === void 0 ? void 0 : pricedStore.Material) !== null && _b !== void 0 ? _b : pricedStore === null || pricedStore === void 0 ? void 0 : pricedStore.material;
    if (material) {
        const materialTotal = (0, exports.getMaterialPricingTotal)(material);
        totalCost += materialTotal;
        const materialMarkups = pricedStore === null || pricedStore === void 0 ? void 0 : pricedStore.material_markup;
        if (materialMarkups) {
            totalCost += (0, exports.calculateMarkupPercentage)(materialMarkups, materialTotal);
        }
    }
    const nonstandard_material = pricedStore === null || pricedStore === void 0 ? void 0 : pricedStore.non_standard_material;
    if (nonstandard_material) {
        const nonstandardMaterialTotal = (0, exports.getNonStandardMaterialPricingTotal)(nonstandard_material);
        totalCost += nonstandardMaterialTotal;
        const nonstandardMaterialMarkups = pricedStore === null || pricedStore === void 0 ? void 0 : pricedStore.non_standard_material_markup;
        if (nonstandardMaterialMarkups) {
            totalCost += (0, exports.calculateMarkupPercentage)(nonstandardMaterialMarkups, nonstandardMaterialTotal);
        }
    }
    return totalCost;
};
exports.getTicketTotalWithExtraLaborQtys = getTicketTotalWithExtraLaborQtys;
/**
 * Given an object with labor, equipment, material, non_standard_material, lower_tier_sub or other_items arrays that include pricing info
 * will return the total value of the whole object, including any supplied markups in all fields
 * @param { Record<string, any> } pricedStore Object with priced LEM arrays and possibly markups
 * @returns { number } Total value of the given pricedStore object
 */
const getTicketTotalWithLowerTierSubAndOtherItems = (pricedStore) => {
    let totalCost = (0, exports.getTicketTotal)(pricedStore);
    const lower_tier_sub = pricedStore === null || pricedStore === void 0 ? void 0 : pricedStore.lower_tier_sub;
    if (lower_tier_sub) {
        const lowerTierSubPricingTotal = (0, exports.getLowerTierSubPricingTotal)(lower_tier_sub);
        totalCost += lowerTierSubPricingTotal;
        const lowerTierSubMarkups = pricedStore === null || pricedStore === void 0 ? void 0 : pricedStore.lower_tier_sub_markup;
        if (lowerTierSubMarkups) {
            totalCost += (0, exports.calculateMarkupPercentage)(lowerTierSubMarkups, lowerTierSubPricingTotal);
        }
    }
    const other_items = pricedStore === null || pricedStore === void 0 ? void 0 : pricedStore.other_items;
    if (other_items) {
        const otherItemsPricingTotal = (0, exports.getOtherItemsPricingTotal)(other_items);
        totalCost += otherItemsPricingTotal;
        const otherItemsMarkups = pricedStore === null || pricedStore === void 0 ? void 0 : pricedStore.other_items_markup;
        if (otherItemsMarkups) {
            totalCost += (0, exports.calculateMarkupPercentage)(otherItemsMarkups, otherItemsPricingTotal);
        }
    }
    return totalCost;
};
exports.getTicketTotalWithLowerTierSubAndOtherItems = getTicketTotalWithLowerTierSubAndOtherItems;
/**
 * A generic helper for basic math operations. By default will return absolute value. If a negative
 * value is desires, set allowNegatives to be true
 * @param { number } lvalue Left side number value for the equation
 * @param { string }operator Equation operator - acceptable options are "+" | "-" | "*" | "/"
 * @param { number } rvalue Right side number value for the equation
 * @param { number } decimals Optional - number of decimals to round to
 * @param { boolean }allowNegatives Optional - default is False - if true will allow negative result
 * @returns { number } result of math operation
 */
const math = (lvalue, operator, rvalue, decimals, allowNegatives = false) => {
    lvalue = (0, utils_1.zeroIfNaN)(lvalue);
    rvalue = (0, utils_1.zeroIfNaN)(rvalue);
    let result = 0;
    switch (operator) {
        case "+":
            result = lvalue + rvalue;
            break;
        case "-":
            const difference = lvalue - rvalue;
            // if we allow negatives don't convert
            if (difference < 0 && !allowNegatives) {
                return difference * -1;
            }
            result = difference;
            break;
        case "*":
            result = lvalue * rvalue;
            break;
        case "/":
            result = lvalue / rvalue;
            break;
        default:
            result = 0;
    }
    if (decimals !== undefined && (0, utils_1.isNumber)(decimals)) {
        result = (0, exports.bankersRound)(result, decimals);
        return result;
    }
    return result;
};
exports.math = math;
